import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import { Answer } from '@/components/common/Answer';
import { AsOfStrip } from '@/components/common/AsOfStrip';
import { ExplainerCard } from '@/components/common/ExplainerCard';
import { ExportButton } from '@/components/common/ExportButton';
import { FieldGuide } from '@/components/common/FieldGuide';
import { ErrorMessage, SkeletonLoader } from '@/components/common/StateViews';
import { OutcomeMixPanel } from '@/pages/anatel/OutcomeMixPanel';
import { fetchAnsNip } from '@/services/ans';

/**
 * The NIP funnel: consumer demands, and how few become a penalty.
 *
 * Counts only, everywhere: nothing about a consumer is loaded, and the
 * funnel measures volume and escalation, never merit. The NIP files run to
 * the current month while the penalties ledger is one snapshot competence,
 * so the last years' penalty share is a floor.
 */
export function AnsNipPage() {
  const { t, i18n } = useTranslation(['ans', 'common']);
  const { data, isPending, error, refetch } = useQuery({
    queryKey: ['ans', 'nip'],
    queryFn: fetchAnsNip,
  });
  const number = new Intl.NumberFormat(i18n.language);
  const percent = new Intl.NumberFormat(i18n.language, {
    style: 'percent',
    maximumFractionDigits: 1,
  });

  if (isPending) {
    return (
      <div className="space-y-4 p-6">
        <SkeletonLoader rows={2} />
        <SkeletonLoader rows={6} />
      </div>
    );
  }
  if (error) {
    return (
      <div className="p-6">
        <ErrorMessage message={error.message} onRetry={() => void refetch()} />
      </div>
    );
  }

  const breakdowns = [
    ['subject', data.by_subject],
    ['nature', data.by_nature],
    ['classification', data.by_classification],
    ['channel', data.by_channel],
  ] as const;

  return (
    <div className="space-y-4 p-6">
      <header className="space-y-1">
        <h1 className="text-lg font-semibold text-foreground">{t('ans:nip.title')}</h1>
        <p className="text-sm text-muted-foreground">{t('ans:nip.subtitle')}</p>
      </header>

      <ExplainerCard i18nKey="ans:nip.explainer" />

      <div className="flex justify-end">
        <ExportButton path="/api/v1/ans/export/nip" label={t('ans:nip.export')} />
      </div>

      <AsOfStrip asOf={data.as_of} />

      <section className="rounded-lg border bg-card p-4" data-funnel>
        <h2 className="mb-3 text-sm font-semibold text-foreground">{t('ans:nip.funnel.title')}</h2>
        <Answer answer={data.funnel} help={t('ans:nip.funnel.help')}>
          {(f) => (
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3 sm:grid-cols-5">
                <Figure label={t('ans:nip.funnel.demands')} value={number.format(f.demands)} />
                <Figure
                  label={t('ans:nip.funnel.escalated')}
                  value={`${number.format(f.escalated)} (${percent.format(f.escalation_share)})`}
                />
                <Figure
                  label={t('ans:nip.funnel.withProcess')}
                  value={number.format(f.with_process)}
                />
                <Figure
                  label={t('ans:nip.funnel.inLedger')}
                  value={`${number.format(f.penalties_in_ledger)} (${percent.format(
                    f.penalty_share,
                  )})`}
                />
                <Figure label={t('ans:nip.funnel.operators')} value={number.format(f.operators)} />
              </div>
              <div className="overflow-x-auto rounded-md border">
                <table className="w-full text-sm">
                  <thead className="bg-muted/50 text-left text-xs text-muted-foreground">
                    <tr>
                      <th className="px-3 py-2 font-medium">{t('ans:nip.funnel.year')}</th>
                      <th className="px-3 py-2 text-right font-medium">
                        {t('ans:nip.funnel.demands')}
                      </th>
                      <th className="px-3 py-2 text-right font-medium">
                        {t('ans:nip.funnel.escalated')}
                      </th>
                      <th className="px-3 py-2 text-right font-medium">
                        {t('ans:nip.funnel.withProcess')}
                      </th>
                      <th className="px-3 py-2 text-right font-medium">
                        {t('ans:nip.funnel.inLedger')}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {f.by_year.map((y) => (
                      <tr key={y.year} className="border-t" data-year={y.year}>
                        <td className="px-3 py-2 tabular-nums text-foreground">{y.year}</td>
                        <td className="px-3 py-2 text-right tabular-nums text-foreground">
                          {number.format(y.demands)}
                        </td>
                        <td className="px-3 py-2 text-right tabular-nums text-muted-foreground">
                          {number.format(y.escalated)} ({percent.format(y.escalation_share)})
                        </td>
                        <td className="px-3 py-2 text-right tabular-nums text-muted-foreground">
                          {number.format(y.with_process)}
                        </td>
                        <td className="px-3 py-2 text-right tabular-nums text-muted-foreground">
                          {number.format(y.penalties_in_ledger)} ({percent.format(y.penalty_share)})
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </Answer>
        <div className="mt-3">
          <FieldGuide i18nKey="ans:nip.guide" />
        </div>
      </section>

      <div className="grid gap-4 lg:grid-cols-2">
        {breakdowns.map(([kind, answer]) => (
          <OutcomeMixPanel
            key={kind}
            answer={{ ...answer, value: answer.value?.rows ?? null }}
            titleKey={`ans:nip.breakdowns.${kind}.title`}
            helpKey={`ans:nip.breakdowns.${kind}.help`}
            helpParams={{ other: answer.value?.other ?? 0, blank: answer.value?.blank ?? 0 }}
            labelPrefix="ans:nip.labels."
          />
        ))}
      </div>

      <section className="rounded-lg border bg-card p-4" data-operators>
        <h2 className="mb-3 text-sm font-semibold text-foreground">
          {t('ans:nip.operators.title')}
        </h2>
        <Answer answer={data.operators} help={t('ans:nip.operators.help')}>
          {(o) => (
            <div className="space-y-2">
              <p className="text-xs text-muted-foreground">
                {t('ans:nip.operators.summary', {
                  operators: number.format(o.operators),
                  demands: number.format(o.demands),
                })}
              </p>
              <ul className="grid grid-cols-5 gap-2">
                {['1-9', '10-99', '100-999', '1000-9999', '10000+'].map((band) => (
                  <li key={band} className="rounded-md border bg-muted/30 p-2" data-band={band}>
                    <div className="text-xs text-muted-foreground">
                      {t('ans:nip.operators.band', { band })}
                    </div>
                    <div className="font-semibold tabular-nums text-foreground">
                      {number.format(o.bands[band] ?? 0)}
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </Answer>
      </section>
    </div>
  );
}

function Figure({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-md border bg-muted/30 p-3">
      <div className="text-xs text-muted-foreground">{label}</div>
      <div className="mt-1 text-base font-semibold tabular-nums text-foreground">{value}</div>
    </div>
  );
}
